import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DefaultValues, useForm, FormProvider } from 'react-hook-form';
import { NUMBER_OF_ROWS } from '@components-shared/table/constants';
import { ColumnSort, SortingState } from '@tanstack/react-table';
import { PaginationState } from '@tanstack/table-core';
import { debounce } from 'lodash-es';
import { GridContextSelectionRowProvider } from './GridContextSelectionRow';
import { gridFiltersService } from './GridHeader/gridFilters.service';

export const useGetFilterValues = () => {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: NUMBER_OF_ROWS[0],
  });

  const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  return {
    pagination,
    setPagination,
  };
};

export type IGenericFiltersData = {
  [filterName in string]: unknown;
};

export type IGridContextPagination = IGridContext<Record<string, unknown>>['pagination'];

export type IGridContextSorting = SortingState;

export interface IGridPagination {
  pageIndex: number;
  pageSize: number;
}

export type IGridFetchData<T extends IGenericFiltersData> = (
  filterOptions: T,
  pagination: IGridPagination,
  sorting: SortingState,
) => Promise<void>;

export interface IGridContext<T extends IGenericFiltersData> {
  getHasSelectedFilters: (filterOptions: T) => boolean;
  pagination: IGridPagination;
  setPagination: Dispatch<SetStateAction<IGridContext<T>['pagination']>>;
  refreshGridData: () => Promise<void>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  defaultSorting: ColumnSort[];
  initialRequest: boolean;
  setInitialRequest: (state: boolean) => void;
  isInitialRequestProcessing: boolean;
  setIsInitialRequestProcessing: (state: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const GridContext = createContext<IGridContext<any>>({
  getHasSelectedFilters: () => false,
  pagination: { pageIndex: -1, pageSize: -1 },
  setPagination: () => 'not defined',
  refreshGridData: () => Promise.resolve(),
  sorting: [],
  setSorting: () => null,
  defaultSorting: [],
  initialRequest: false,
  setInitialRequest: () => void 0,
  isInitialRequestProcessing: false,
  setIsInitialRequestProcessing: () => void 0,
});

export const useGridContext = () => {
  const context = useContext(GridContext);

  if (!context) {
    throw new Error('useGridContext must be used within a GridContextProvider');
  }

  return context;
};
const defaultSortingById = [{ id: 'id', desc: true }];

export const GridContextProvider = <T extends IGenericFiltersData>({
  children,
  defaultFilters,
  fetchData,
  accessChecked,
  navigateToFirstPageOnDepsChange = [],
  defaultSorting,
  hasInitialRequest = false,
}: {
  children: ReactNode;
  defaultFilters?: DefaultValues<T>;
  fetchData: IGridFetchData<T>;
  accessChecked?: boolean;
  navigateToFirstPageOnDepsChange?: React.DependencyList | undefined;
  defaultSorting?: ColumnSort[];
  hasInitialRequest?: boolean;
}) => {
  const methods = useForm<T>({ defaultValues: defaultFilters });

  const { pagination, setPagination } = useGetFilterValues();

  const initialSorting = useMemo(() => (defaultSorting ? defaultSorting : defaultSortingById), [defaultSorting]);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const [initialRequest, setInitialRequest] = React.useState(hasInitialRequest);
  const [isInitialRequestProcessing, setIsInitialRequestProcessing] = useState(hasInitialRequest);

  const { getValues } = methods;

  const getHasSelectedFilters = useCallback(
    (filterOptions: T) => gridFiltersService.getHasSelectedFilters(defaultFilters ?? {}, filterOptions),
    [defaultFilters],
  );

  const refreshGridData = useCallback(
    (options?: { shouldNavigateToFirstPage?: boolean }) => {
      if (options?.shouldNavigateToFirstPage) {
        const firstPagePagination = { pageIndex: 0, pageSize: pagination.pageSize };

        setPagination(firstPagePagination);
        return fetchData(getValues(), firstPagePagination, sorting);
      }

      setInitialRequest(true);
      return fetchData(getValues(), pagination, sorting);
    },
    [fetchData, getValues, pagination, setPagination, sorting],
  );

  const context = useMemo(
    () => ({
      getHasSelectedFilters,
      pagination,
      setPagination,
      refreshGridData,
      sorting,
      initialRequest,
      setInitialRequest,
      isInitialRequestProcessing,
      setIsInitialRequestProcessing,
      setSorting,
      defaultSorting: initialSorting,
    }),
    [
      getHasSelectedFilters,
      initialRequest,
      initialSorting,
      isInitialRequestProcessing,
      pagination,
      refreshGridData,
      setPagination,
      sorting,
    ],
  );

  useEffect(() => {
    if (navigateToFirstPageOnDepsChange.length === 0) {
      return;
    }
    void refreshGridData({ shouldNavigateToFirstPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, navigateToFirstPageOnDepsChange ?? []);

  const debouncedRefreshGridData = useMemo(
    () => debounce(refreshGridData, 300, { leading: false, trailing: true }),
    [refreshGridData],
  );

  useEffect(() => {
    if (initialRequest) void debouncedRefreshGridData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, accessChecked]);

  useEffect(() => {
    return () => {
      debouncedRefreshGridData.cancel();
    };
  }, [debouncedRefreshGridData]);

  return (
    <GridContext.Provider value={context}>
      <GridContextSelectionRowProvider>
        <FormProvider {...methods}>{children}</FormProvider>
      </GridContextSelectionRowProvider>
    </GridContext.Provider>
  );
};
